import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, catchError, finalize, shareReplay, tap, throwError } from 'rxjs';
import { ICompanyResponse } from '../../../core/models/company.model';
import { IAISummaryResponse, IInventionDataResponse, IKeywordsResponse, IPeerGroupCompetitorsResponse, ITopCompaniesResponse, IInnovationMetricsSnapshotResponse, IInnovationMetricsHistoryResponse, IThematicPerformanceResponse, IThematicInnovationActivityResponse, IThematicInnovationMetricKey, IThemeExecSummaryResponse, IStageTopFirmsResponse, IThematicTechSummaryResponse, ICompanyThemeActivityResponse, IClusterMetadataResponse } from '../../../core/models/results.model';
import { loadSidenavData } from 'src/app/state/action';
import { AppState, IUniverseSelection } from 'src/app/state/app.state';


@Injectable({
  providedIn: 'root'
})
export class GeneralInnovationActivityResultsService {

  private inflight = new Map<string, Observable<any>>();

  constructor(private http: HttpClient, private store: Store<AppState>) { }

  private dedupe<T>(
    key: string,
    factory: () => Observable<T>,
    onSuccess?: (res: T) => void,
  ): Observable<T> {
    const existing = this.inflight.get(key) as Observable<T> | undefined;
    if (existing) return existing;
    const req$ = factory().pipe(
      tap(res => onSuccess?.(res)),
      finalize(() => this.inflight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inflight.set(key, req$);
    return req$;
  }

  invalidateInflight(key: string): void {
    this.inflight.delete(key);
  }

  inventionsKey(p: {
    query: string | null | undefined;
    entityids: string[] | null | undefined;
    tabId: string;
    view: string;
    theme_search_type?: string | null;
    cluster_id?: number | string | null;
    date_start?: Date | null;
    date_end?: Date | null;
    quality_filter?: string | null;
  }): string {
    const ids = (p.entityids ?? []).join(',');
    const ds = p.date_start ? new Date(p.date_start).toISOString() : '';
    const de = p.date_end ? new Date(p.date_end).toISOString() : '';
    return `inventions:${p.tabId}:${p.view}:${p.query ?? ''}:${ids}:${p.theme_search_type ?? ''}:${p.cluster_id ?? ''}:${ds}:${de}:${p.quality_filter ?? ''}`;
  }

  thematicPerformanceHistoryKey(p: {
    cluster_id: string | null;
    cluster_name: string | null;
    query: string | null | undefined;
    theme_search_type: string;
    tabId: string;
  }): string {
    return `thematicPerfHistory:${p.tabId}:${p.theme_search_type}:${p.cluster_id ?? ''}:${p.cluster_name ?? ''}:${p.query ?? ''}`;
  }

  themeExecSummaryKey(p: {
    cluster_id: string | null;
    cluster_name: string | null;
    theme_search_type: string;
    query: string | null | undefined;
    tabId: string;
  }): string {
    return `themeExecSummary:${p.tabId}:${p.theme_search_type}:${p.cluster_id ?? ''}:${p.cluster_name ?? ''}:${p.query ?? ''}`;
  }


  getTopCompanies(params: {
    cluster_id: string | null,
    cluster_name: string | null,
    query: string | null | undefined,
    theme_search_type: string,
    tabId: string,
    include_public?: boolean,
    include_private?: boolean,
    min_mcap?: number,
    max_mcap?: number
  }):  Observable<HttpResponse<ITopCompaniesResponse>> {
    const key = `topCompanies:${params.tabId}:${params.theme_search_type}:${params.cluster_id ?? ''}:${params.cluster_name ?? ''}:${params.query ?? ''}:${params.include_public ?? ''}:${params.include_private ?? ''}:${params.min_mcap ?? ''}:${params.max_mcap ?? ''}`;
    return this.dedupe<HttpResponse<ITopCompaniesResponse>>(key, () =>
      this.http.post<ITopCompaniesResponse>(
      '/backend/thematicView/top_companies',
      {
        cluster_id: params.cluster_id,
        cluster_name: params.cluster_name,
        query: params.query,
        theme_search_type: params.theme_search_type,
        tabId: params.tabId,
        include_public: params.include_public ?? false,
        include_private: params.include_private ?? false,
        min_mcap: params.min_mcap,
        max_mcap: params.max_mcap,
      },
      {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('current_user_token'),
        },
        observe: 'response',
        responseType: 'json',
      }
    ));
  }

  // --- Top companies history (custom themes): years → prepare each year → download xlsx ---

  private get authHeaders() {
    return { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') };
  }

  getTopCompaniesHistoryYears(): Observable<{ payload: { years: number[]; date_eom: string } }> {
    return this.http.get<{ payload: { years: number[]; date_eom: string } }>(
      '/backend/thematicView/top_companies/history/years', { headers: this.authHeaders });
  }

  prepareTopCompaniesHistory(query: string, year: number | null): Observable<unknown> {
    return this.http.post('/backend/thematicView/top_companies/history/prepare',
      { query, year }, { headers: this.authHeaders });
  }

  downloadTopCompaniesHistory(query: string, years: (number | null)[]): Observable<HttpResponse<Blob>> {
    return this.http.post('/backend/thematicView/top_companies/history/download',
      { query, years }, { headers: this.authHeaders, observe: 'response', responseType: 'blob' });
  }





  getInventions(
    query: string | null | undefined,
    entityids: string[] | null | undefined,
    date_start: Date | null | undefined,
    date_end: Date | null | undefined,
    quality_filter: string | null | undefined,
    view: string,
    tabId: string,
    themeParams?: { theme_search_type?: string; cluster_id?: number | string | null; cluster_name?: string | null },
  ): Observable<IInventionDataResponse> {
    const key = this.inventionsKey({
      query, entityids, tabId, view,
      theme_search_type: themeParams?.theme_search_type ?? null,
      cluster_id: themeParams?.cluster_id ?? null,
      date_start: date_start ?? null,
      date_end: date_end ?? null,
      quality_filter: quality_filter ?? null,
    });
    return this.dedupe<IInventionDataResponse>(
      key,
      () => this.http.post<IInventionDataResponse>('/backend/results/inventions/data', {
        query, entityids, date_end, date_start, quality_filter, view, tabId,
        theme_search_type: themeParams?.theme_search_type ?? null,
        cluster_id: themeParams?.cluster_id ?? null,
        cluster_name: themeParams?.cluster_name ?? null,
      }, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("current_user_token"),
        },
        responseType: 'json',
      }),
    );
  }


  getAISummary(
    query: string | null | undefined,
    entityids: string[] | null | undefined,
    date_start: Date | null | undefined,
    date_end: Date | null | undefined,
    quality_filter: string | null | undefined,
    view: string,
    tabId: string,
    themeParams?: { theme_search_type?: string; cluster_id?: number | string | null; cluster_name?: string | null },
  ): Observable<IAISummaryResponse> {
    return this.http.post<IAISummaryResponse>('/backend/siaAssistant/summary', {
      query, entityids, date_end, date_start, quality_filter, view, tabId,
      theme_search_type: themeParams?.theme_search_type ?? null,
      cluster_id: themeParams?.cluster_id ?? null,
      cluster_name: themeParams?.cluster_name ?? null,
    }, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  getInnovationActivityByCompany(params: {
    entityid: string;
    theme_search_type: string;
    cluster_id?: number | string | null;
    cluster_name?: string | null;
    query?: string | null;
    stat_term: IThematicInnovationMetricKey[];
    tabId: string;
  }): Observable<HttpResponse<ICompanyThemeActivityResponse>> {
    return this.http.post<ICompanyThemeActivityResponse>(
      '/backend/thematicView/innovation/activity',
      params,
      {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
        observe: 'response',
        responseType: 'json',
      },
    );
  }

  getInnovationSnapshot(entityid: string, tabId: string, filters?: IUniverseSelection): Observable<HttpResponse<IInnovationMetricsSnapshotResponse>> {
    let params = new HttpParams().set('tabId', tabId);
    if (filters) {
      params = params
        .set('include_public', String(filters.include_public ?? true))
        .set('include_private', String(filters.include_private ?? true));
      if (filters.min_mcap != null) params = params.set('min_mcap', String(filters.min_mcap));
      if (filters.max_mcap != null) params = params.set('max_mcap', String(filters.max_mcap));
    }
    const f = filters ?? {} as Partial<IUniverseSelection>;
    const key = `innovationSnapshot:${tabId}:${entityid}:${f.include_public ?? ''}:${f.include_private ?? ''}:${f.min_mcap ?? ''}:${f.max_mcap ?? ''}`;
    return this.dedupe<HttpResponse<IInnovationMetricsSnapshotResponse>>(key, () =>
      this.http.get<IInnovationMetricsSnapshotResponse>(`/backend/innovationMetrics/snapshot/${entityid}`, {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
        params,
        observe: 'response',
        responseType: 'json',
      }),
    );
  }

  getInnovationHistory(entityid: string, tabId: string): Observable<HttpResponse<IInnovationMetricsHistoryResponse>> {
    const key = `innovationHistory:${tabId}:${entityid}`;
    return this.dedupe<HttpResponse<IInnovationMetricsHistoryResponse>>(key, () =>
      this.http.get<IInnovationMetricsHistoryResponse>(`/backend/innovationMetrics/history/${entityid}`, {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
        params: new HttpParams().set('tabId', tabId),
        observe: 'response',
        responseType: 'json',
      }),
    );
  }

  getThematicPerformanceHistory(params: {
    cluster_id: string | null;
    cluster_name: string | null;
    query: string | null | undefined;
    theme_search_type: string;
    tabId: string;
  }): Observable<HttpResponse<IThematicPerformanceResponse>> {
    const key = this.thematicPerformanceHistoryKey(params);
    return this.dedupe<HttpResponse<IThematicPerformanceResponse>>(
      key,
      () => this.http.post<IThematicPerformanceResponse>(
        '/backend/thematicView/performance/history',
        params,
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          observe: 'response',
          responseType: 'json',
        }
      ),
      res => {
        if (!res.ok) return;
        const payload = res.body?.payload;
        const data = payload?.data;
        if (!data?.length) return;
        const targetTabId = payload?.tabId ?? params.tabId;
        this.store.dispatch(loadSidenavData({
          tabId: targetTabId,
          sidenavKey: 'thematic_performance_history',
          data,
        }));
      },
    );
  }

  getCompanyTechnologySummary(entityid: string, tabId: string): Observable<HttpResponse<any>> {
    const key = `companyTechSummary:${tabId}:${entityid}`;
    return this.dedupe<HttpResponse<any>>(key, () =>
      this.http.post<any>(
        '/backend/thematicView/company/technology/summary',
        { entityid, tabId },
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          observe: 'response',
          responseType: 'json',
        }
      ),
    );
  }

  getThematicInnovationActivity(params: {
    cluster_id: string | null;
    cluster_name: string | null;
    query: string | null | undefined;
    theme_search_type: string;
    stat_term: IThematicInnovationMetricKey[];
    tabId: string;
  }): Observable<HttpResponse<IThematicInnovationActivityResponse>> {
    const key = `thematicInnovActivity:${params.tabId}:${params.theme_search_type}:${params.cluster_id ?? ''}:${params.cluster_name ?? ''}:${params.query ?? ''}:${(params.stat_term ?? []).join(',')}`;
    return this.dedupe<HttpResponse<IThematicInnovationActivityResponse>>(key, () =>
      this.http.post<IThematicInnovationActivityResponse>(
        '/backend/thematicView/innovation/activity',
        params,
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          observe: 'response',
          responseType: 'json',
        }
      ),
    );
  }

  getThemeExecSummary(params: {
    cluster_id: string | null;
    cluster_name: string | null;
    theme_search_type: string;
    query: string | null | undefined;
    tabId: string;
  }): Observable<HttpResponse<IThemeExecSummaryResponse>> {
    const key = this.themeExecSummaryKey(params);
    return this.dedupe<HttpResponse<IThemeExecSummaryResponse>>(
      key,
      () => this.http.post<IThemeExecSummaryResponse>(
        '/backend/thematicView/exec-summary',
        params,
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          observe: 'response',
          responseType: 'json',
        }
      ),
      res => {
        if (!res.ok) return;
        const payload = res.body?.payload;
        if (!payload?.data) return;
        const targetTabId = payload?.tabId ?? params.tabId;
        this.store.dispatch(loadSidenavData({
          tabId: targetTabId,
          sidenavKey: 'thematic_exec_summary',
          data: payload.data,
        }));
      },
    );
  }

  getStageTopFirms(stage: number, tabId: string): Observable<HttpResponse<IStageTopFirmsResponse>> {
    return this.http.post<IStageTopFirmsResponse>(
      '/backend/thematicView/stage-top-firms',
      { stage, tabId },
      {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  getThematicTechnologySummary(params: {
    cluster_id: string | null;
    cluster_name: string | null;
    query: string | null | undefined;
    theme_search_type: string;
    tabId: string;
  }): Observable<HttpResponse<IThematicTechSummaryResponse>> {
    const key = `thematicTechSummary:${params.tabId}:${params.theme_search_type}:${params.cluster_id ?? ''}:${params.cluster_name ?? ''}:${params.query ?? ''}`;
    return this.dedupe<HttpResponse<IThematicTechSummaryResponse>>(key, () =>
      this.http.post<IThematicTechSummaryResponse>(
        '/backend/thematicView/theme/technology/summary',
        params,
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          observe: 'response',
          responseType: 'json',
        }
      ),
    );
  }

  getClusterMetadata(params: {
    cluster_id: string;
    tabId: string;
  }): Observable<HttpResponse<IClusterMetadataResponse>> {
    const key = `clusterMetadata:${params.tabId}:${params.cluster_id}`;
    return this.dedupe<HttpResponse<IClusterMetadataResponse>>(key, () =>
      this.http.post<IClusterMetadataResponse>(
        '/backend/thematicView/cluster/metadata',
        params,
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          observe: 'response',
          responseType: 'json',
        }
      ),
    );
  }

}
